import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined'
import { Image, Popover, Spin, Tabs, Typography } from 'antd'
import Table, { ColumnsType } from 'antd/es/table'
import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

import {
  NiiData,
  NiiSourcesParams,
  NiiSourcesZoneParams,
} from './NiiDetailsModal'

export interface NiiSourcesRightPanelProps {
  entryId: string
  niiData: NiiData
  setHoveredParam: (param: keyof NiiSourcesParams | null) => void
  unsourcedParams: ReadonlyArray<keyof NiiSourcesParams>
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex-wrap: wrap;
`

const Panel = styled.div`
  flex: 1;
  overflow: auto;
`

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const TableContainer = styled.div`
  padding-top: 20px;
  padding-bottom: 20px;
`

const NII_SOURCES_PARAMS_LABELS: Record<keyof NiiSourcesParams, string> = {
  commission_date: 'Commission Date',
  design_code: 'Design Code',
  contents: 'Contents',
  external_coating: 'External Coating',
  insulation: 'Insulation',
  design_pressure_bar: 'Design Pressure (bar)',
  operating_pressure_bar: 'Operating Pressure (bar)',
  design_temperature_c: 'Design Temp. (C)',
  max_operating_temperature_c: 'Max Operating Temp (C)',
  internal_inspection_interval_yrs: 'Next inspection Interval (yrs)',
  next_internal_inspection: 'Next Internal Inspection',
}

const NII_SOURCES_ZONES_PARAMS_LABELS: Record<
  keyof NiiSourcesZoneParams,
  string
> = {
  material: 'Material',
  geometry: 'Zone Geometry',
  length_mm: 'Length (mm)',
  height_mm: 'Height (mm)',
  internal_diameter_mm: 'Internal Diameter (mm)',
  external_diameter_mm: 'External Diameter (mm)',
  total_surface_area_sq_m: 'Total Surface Area (sq. m)',
  wall_thickness_shell_mm: 'Wall Thickness (shell) (mm)',
  wall_thickness_heads_mm: 'Wall Thickness (heads) (mm)',
  corrosion_allowance_mm: 'Corrosion Allowance (mm)',
}

type ParameterRecord<T> = {
  parameter: keyof T
  value: string | number
}

const UnsourcedParamIcon: FC = () => {
  return (
    <Popover title='No source document found for this param'>
      <ExclamationCircleOutlined
        style={{ color: CantileverTheme.colors.BrightOrange, fontSize: 16 }}
        twoToneColor={CantileverTheme.colors.BrightOrange}
      />
    </Popover>
  )
}

function parameterTableCols<T>(
  labels: Record<keyof T, string>,
  unsourcedParameters?: ReadonlyArray<keyof NiiSourcesParams>
): ColumnsType<ParameterRecord<T>> {
  return [
    {
      title: 'Parameter',
      key: 'parameter',
      width: '14rem',
      render: (record: ParameterRecord<T>) => {
        return (
          <>
            {labels[record.parameter]}
            {unsourcedParameters?.includes(
              record.parameter as keyof NiiSourcesParams
            ) && (
              <span style={{ paddingLeft: '1rem' }}>
                <UnsourcedParamIcon />
              </span>
            )}
          </>
        )
      },
    },
    {
      title: 'Value',
      key: 'value',
      width: '14rem',
      render: (record: ParameterRecord<T>) => record.value,
    },
  ]
}

const { Title } = Typography

export const NiiSourcesRightPanel: FC<NiiSourcesRightPanelProps> = ({
  entryId,
  niiData,
  setHoveredParam,
  unsourcedParams,
}) => {
  return (
    <Container>
      {niiData?.params && (
        <NiiParamsSection
          params={niiData.params}
          zoneParams={niiData.zone_params ?? []}
          setHoveredParam={setHoveredParam}
          unsourcedParams={unsourcedParams}
        />
      )}
      <Panel>
        <ZonesSection entryId={entryId} />
      </Panel>
    </Container>
  )
}

const NiiParamsSection: FC<{
  params: NiiSourcesParams
  zoneParams: ReadonlyArray<NiiSourcesZoneParams>
  setHoveredParam: (param: keyof NiiSourcesParams | null) => void
  unsourcedParams: ReadonlyArray<keyof NiiSourcesParams>
}> = ({ params, zoneParams, setHoveredParam, unsourcedParams }) => {
  const dataSource = useMemo<ReadonlyArray<ParameterRecord<NiiSourcesParams>>>(
    () =>
      Object.entries(params).map(([param, value]) => {
        return {
          parameter: param as keyof NiiSourcesParams,
          value: value.content,
        }
      }),
    [params]
  )

  const zoneDataSources = useMemo<
    ReadonlyArray<ReadonlyArray<ParameterRecord<NiiSourcesZoneParams>>>
  >(
    () =>
      zoneParams.map((zoneParam) =>
        Object.entries(zoneParam).map(([param, value]) => {
          return {
            parameter: param as keyof NiiSourcesZoneParams,
            value,
          }
        })
      ),
    [zoneParams]
  )

  const tabItems = useMemo(
    () => [
      {
        label: 'Design and Function',
        key: '0',
        children: (
          <TableContainer>
            <Title level={3}>Design and Function</Title>
            <Table
              columns={parameterTableCols<NiiSourcesParams>(
                NII_SOURCES_PARAMS_LABELS,
                unsourcedParams
              )}
              dataSource={dataSource}
              rowKey={(row) => row.parameter}
              pagination={false}
              onRow={(record) => ({
                onMouseEnter: () => setHoveredParam(record.parameter),
                onMouseLeave: () => setHoveredParam(null),
              })}
            />
          </TableContainer>
        ),
      },
      ...zoneDataSources.map((data, idx) => ({
        label: `Zone ${idx + 1}`,
        key: `zone_${idx + 1}`,
        children: (
          <TableContainer>
            <Title level={3}>Zone {idx + 1}</Title>
            <Table
              columns={parameterTableCols<NiiSourcesZoneParams>(
                NII_SOURCES_ZONES_PARAMS_LABELS
              )}
              dataSource={data}
              rowKey={(row) => row.parameter}
              pagination={false}
            />
          </TableContainer>
        ),
      })),
    ],
    [dataSource, setHoveredParam, unsourcedParams, zoneDataSources]
  )
  return (
    <SectionContainer>
      <Title>Parameters</Title>
      <Tabs defaultActiveKey='0' items={tabItems} />
    </SectionContainer>
  )
}

const ZonesSection: FC<{ entryId: string }> = ({ entryId }) => {
  const { fileRefs, isLoading: zonesFilesLoading } = useRolodexFileLS(
    useMemo(
      () => ({
        entry_id: entryId,
        path: '/zones.png',
      }),
      [entryId]
    )
  )

  const { urls: zonesUrls, isLoading: zonesUrlsLoading } =
    useRolodexSignedURLs(fileRefs)

  const zonesLoading = zonesFilesLoading || zonesUrlsLoading

  return (
    <SectionContainer>
      <Title>Zones</Title>
      <BodyContainer>
        <Spin spinning={zonesLoading}>
          <Image width={400} src={zonesUrls[0]} />
        </Spin>
      </BodyContainer>
    </SectionContainer>
  )
}
